/**
 * StyledSelect — a single-select dropdown styled to match the app theme,
 * replacing the native `<select>` for form fields.
 *
 * A styled trigger button (reuses the field class passed via `className`,
 * e.g. `admin-input` / `aurora-input`) plus the shared, portaled
 * {@link AutocompleteListbox} popover for the option list. The popover
 * portals to `document.body` and is anchored to the trigger, so it escapes
 * modal/overflow clipping and sits above modals (see the `.ac-listbox`
 * z-index).
 *
 * Controlled: `value` is the selected option's `value` (use `''` for a
 * "none"/"any" option). Keyboard: ↑/↓ open + move, Enter/Space select,
 * Home/End jump, Esc close.
 */
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { AutocompleteListbox } from '@/components/common/popover/AutocompleteListbox';

export interface StyledSelectOption {
  value: string;
  label: string;
}

interface StyledSelectProps {
  value: string;
  options: StyledSelectOption[];
  onChange: (value: string) => void;
  /** Field-styling class to match surrounding inputs (default `admin-input`). */
  className?: string;
  /** Shown when no option matches `value`. */
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

export default function StyledSelect({
  value,
  options,
  onChange,
  className = 'admin-input',
  placeholder = 'Select…',
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: Readonly<StyledSelectProps>) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalKey = useId();

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const openMenu = () => {
    const idx = options.findIndex((o) => o.value === value);
    setActiveIndex(Math.max(idx, 0));
    setOpen(true);
  };

  const pick = (opt: StyledSelectOption) => {
    onChange(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openMenu();
        else setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openMenu();
        else setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        if (open) { e.preventDefault(); setActiveIndex(0); }
        break;
      case 'End':
        if (open) { e.preventDefault(); setActiveIndex(options.length - 1); }
        break;
      case 'Enter':
      case ' ':
        // When open, select the active row; when closed, let the native button
        // click (onClick) open the menu so we don't toggle twice.
        if (open) {
          e.preventDefault();
          const opt = options[activeIndex];
          if (opt) pick(opt);
        }
        break;
      case 'Escape':
        if (open) { e.preventDefault(); setOpen(false); }
        break;
      case 'Tab':
        if (open) setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        className={`${className} flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={`min-w-0 truncate ${selected ? '' : 'opacity-50'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`shrink-0 text-xs opacity-60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AutocompleteListbox<StyledSelectOption>
        anchorRef={triggerRef}
        open={open}
        items={options}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        onSelect={(opt) => pick(opt)}
        getItemKey={(opt) => opt.value}
        isItemSelected={(opt) => opt.value === value}
        renderItem={(opt) => <span className="min-w-0 truncate">{opt.label}</span>}
        onRequestClose={() => setOpen(false)}
        dataPortalKey={`styled-select-${portalKey}`}
      />
    </>
  );
}
